import {Component, OnDestroy, OnInit} from '@angular/core';
import {FacadeService} from "../../service-facade/facade.service";
import {ProcessService} from "../../../process/process.service";
import {ApplicantControllerService, ApplicantIdentityResponseRequest, ApplicantRequest, ApplicationDetailLog, ApplicationLogsRequest, ApplicationLogsUpdateRequest} from "leia-api";
import {
  KycControllerService,
} from "skyhopper-api";
import ServiceTypeEnum = ApplicantIdentityResponseRequest.ServiceTypeEnum;
import {AlloyOutcome} from "../../model/alloy-outcome";
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { MatTabChangeEvent } from '@angular/material/tabs';
import StatusEnum = ApplicationLogsUpdateRequest.StatusEnum;
import { CoreService } from '../../services/core.service';
import {
  ERROR_PATH
} from "../../constants";
import { ReferenceCode } from 'src/app/process/models/referenceCode';
import { AlloyApplicantStatus } from '../../model/alloy-applicant-status';
import { from, merge, Observable, of, Subject, Subscription, timer } from 'rxjs';
import { catchError, exhaustMap, finalize, switchMap, takeUntil, tap } from 'rxjs/operators';
import { NGXLogger } from 'ngx-logger';


@Component({
  selector: 'app-alloy-journey',
  templateUrl: './alloy-journey.component.html',
  styleUrls: ['./alloy-journey.component.scss']
})
export class AlloyJourneyComponent implements OnInit, OnDestroy {

  applicantRequests: ApplicantRequest[] = new Array();
  journeyToken: string = "";
  sessionId: string = "";
  journeyApplicationToken: string = "";
  alloyEntityTokenIdMap: Map<number, string> = new Map<number, string>();
  activeTabIndex: number = 0;
  alloyJourneyStatusRefreshIntervalMap: Map<number, Subscription> = new Map<number, Subscription>();
  alloyFrameIsNotLoadedMap: Map<number, boolean> = new Map<number, boolean>();
  alloyJourneyApplicantStatusMap: Map<number, any> = new Map<number, any>();
  private destroySubject$: Subject<void> = new Subject();
  // Terminal routing is shared by all applicants and runs outside the polling streams.
  private terminalRouteIsDenied: boolean | null = null;
  private terminalRouteSubscription?: Subscription;

  constructor(private facadeService: FacadeService,
              private processService: ProcessService,
              private applicantControllerService: ApplicantControllerService,
              private sanitizer: DomSanitizer,
              private coreService: CoreService,
              private kycService: KycControllerService,
              private logger: NGXLogger) {
  }

  ngOnInit() {
    this.initiateJourneyApplication();
  }

  private initiateJourneyApplication() {
    this.sessionId = this.facadeService.getSessionId();

    if (!this.sessionId) {
      return;
    }

    this.getKycApplicants().forEach(applicant => {
      this.applicantRequests.push(applicant);
      this.alloyFrameIsNotLoadedMap.set(applicant.applicantId!, true);
      this.processService.alloyDocVOutcomeMap.delete(applicant.applicantId!);

      if (!this.processService.alloyByPassEvaluationMap.has(applicant.applicantId!)) {
        this.processService.alloyEvaluateOutcomeMap.delete(applicant.applicantId!);
      }
    });

    if(this.applicantRequests.length === 0) {
      return;
    }

    this.kycService.createJourney(this.sessionId).pipe(
      catchError(err => {
        this.logError('Create journey', err);
        return of(err);
      }),
      tap(createJourneyResponse => this.journeyApplicationToken = createJourneyResponse.journey_application_token!)
    ).subscribe();
    
    this.pollApplicantIdentityResponse();
  }

  private pollApplicantIdentityResponse() {
    this.applicantRequests.forEach((applicant, applicantArrayIndex) => {
      let currentKycStatus: string = "";
      let currentDocVStatus: string = "";
      let currentJourneyStatus: string = "";

      // Poll for identity responses every 5 seconds
      const statusSubscription = timer(0, 5000)
        .pipe(
          takeUntil(this.destroySubject$),
          // exhaustMap: a slow terminal routing call must not be cancelled by the next 5s tick.
          exhaustMap(() => {
            const getIdentityResponse = (serviceType: ServiceTypeEnum) =>
              this.applicantControllerService
                .getRecentApplicantIdentityResponseByServiceTypeId(applicant.applicantId!, serviceType)
                .pipe(
                  catchError(err => {
                    this.logError(`Poll identity response (service type ${serviceType})`, err);
                    return of(null);
                  })
                );
            return merge(
              // AlloyKyc (service type 1) is the KYC decision; service type 18 is journey metadata.
              getIdentityResponse(ServiceTypeEnum.AlloyKyc).pipe(
                switchMap(response => {
                  if (!response?.outcome || currentKycStatus === response.outcome) return of(null);

                  currentKycStatus = response.outcome;
                  return this.processAlloyJourneyCallback(currentKycStatus, applicant, ServiceTypeEnum.AlloyKyc);
                })
              ),
              // Handle AlloyDocV responses
              getIdentityResponse(ServiceTypeEnum.AlloyDocV).pipe(
                switchMap(response => {
                  if (!response) return of(null);
                  
                  // Handle status change
                  if (response.outcome && currentDocVStatus !== response.outcome) {
                    currentDocVStatus = response.outcome;
                    
                    if (currentDocVStatus === AlloyOutcome.Approved) {
                      const applicantId = applicant.applicantId!;
                      const existingStatus = this.alloyJourneyApplicantStatusMap.get(applicantId) as AlloyApplicantStatus | undefined;
                      const alloyApplicantStatus =
                        existingStatus ?? new AlloyApplicantStatus(applicantId, currentDocVStatus, "", "");

                      alloyApplicantStatus.status = currentDocVStatus;
                      this.alloyJourneyApplicantStatusMap.set(applicantId, alloyApplicantStatus);
                      this.alloyFrameIsNotLoadedMap.set(applicantId, false);
                    }

                    return this.processAlloyJourneyCallback(currentDocVStatus, applicant, ServiceTypeEnum.AlloyDocV);
                  }
                  
                  // Handle tab switching logic
                  if (response?.outcome && this.applicantRequests.length > 1) {
                    if ((this.activeTabIndex === 0 && applicantArrayIndex === 0) || 
                        (this.activeTabIndex === 1 && applicantArrayIndex === 1)) {
                      this.activeTabIndex = this.activeTabIndex === 0 ? 1 : 0;
                    }
                  }
                  
                  return of(null);
                })
              ),
              // Handle AlloyJourney responses
              getIdentityResponse(ServiceTypeEnum.AlloyJourney).pipe(
                switchMap(response => {
                  if (!response) return of(null);
                  
                  // Validate journey application token
                  if (response.journeyApplicationId && this.journeyApplicationToken !== response.journeyApplicationId) {
                    return of(null);
                  }
                  
                  // Setup QR code if available and frame not loaded
                  if (response?.referenceUrl && this.alloyFrameIsNotLoadedMap.get(applicant.applicantId!)) {
                    const alloyApplicantStatus = new AlloyApplicantStatus(
                      applicant.applicantId!, "", "", ""
                    );
                    
                    alloyApplicantStatus.qrCode = response.referenceUrl;
                    alloyApplicantStatus.eventId = response.eventId!;

                    this.alloyJourneyApplicantStatusMap.set(applicant.applicantId!, alloyApplicantStatus);
                    this.alloyFrameIsNotLoadedMap.set(applicant.applicantId!, false);
                  }

                  // Handle status change
                  if (response.outcome && currentJourneyStatus !== response.outcome) {
                    currentJourneyStatus = response.outcome;
                    return this.processAlloyJourneyCallback(currentJourneyStatus, applicant, ServiceTypeEnum.AlloyJourney);
                  }
                  
                  return of(null);
                })
              )
            );
          })
        )
        .subscribe();
      this.alloyJourneyStatusRefreshIntervalMap.set(applicant.applicantId!, statusSubscription);
    });
  }

  private getReferenceCodeFromRcCode(rcCode: string | undefined): ReferenceCode {
    const rcCodeMap: Record<string, ReferenceCode> = {
      'RC200': ReferenceCode.AlloyUnderReview,
      'RC201': ReferenceCode.DocVUnderReview,
      'RC210': ReferenceCode.FiservEnrollmentFailure,
      'RC211': ReferenceCode.JointAccountEnrollmentFailure,
      'RC220': ReferenceCode.OnlineBankingEnrollmentFailure,
      'RC230': ReferenceCode.UnderReviewFailure,
      'RC300': ReferenceCode.AlloyDenied,
      'RC301': ReferenceCode.DocVDenied,
      'RC303': ReferenceCode.AlloyQualifileDecline,
      'RC310': ReferenceCode.StatusUFailure,
      'RC330': ReferenceCode.EligibilityReviewFailure,
      'RC340': ReferenceCode.InternalFailure,
      'RC350': ReferenceCode.AlloyFailure,
      'RC351': ReferenceCode.DocVFailure,
      'RC360': ReferenceCode.SocureFailure,
      'RC370': ReferenceCode.EligibilityFailure,
      'RC380': ReferenceCode.EMailSSNCombinationInvalid,
      'RC390': ReferenceCode.ApplicantInfoMismatch,
      'RC400': ReferenceCode.ProductCheckFailure,
      'RC401': ReferenceCode.AccountRulesFailure,
      'RC402': ReferenceCode.HYSAAccountRulesFailure
    };

    return rcCodeMap[rcCode || ''] || ReferenceCode.GeneralFailure;
  }

  private processAlloyJourneyCallback(
    status: string,
    applicant: ApplicantRequest,
    serviceType: ServiceTypeEnum
  ): Observable<any> {
    if (serviceType === ServiceTypeEnum.AlloyKyc) {
      this.processService.alloyEvaluateOutcomeMap.set(applicant.applicantId!, status);
    } else if (serviceType === ServiceTypeEnum.AlloyDocV) {
      this.processService.alloyDocVOutcomeMap.set(applicant.applicantId!, status);
    }

    if (serviceType !== ServiceTypeEnum.AlloyKyc && this.alloyJourneyApplicantStatusMap.has(applicant.applicantId!)) {
      let existingStatusMap = this.alloyJourneyApplicantStatusMap.get(applicant.applicantId!);
      existingStatusMap.status = status;

      this.alloyJourneyApplicantStatusMap.set(applicant.applicantId!, existingStatusMap);
    }

    let applicationLogRequest = this.facadeService.getShoppingCartApplicationLogsRequest();

    const kycApplicants = this.getKycApplicants();
    const isJoint = kycApplicants.length > 1;

    // Joint: Leia also copies a KYC denial onto the Journey row; the KYC row drives routing.
    if (isJoint && serviceType === ServiceTypeEnum.AlloyJourney && status === AlloyOutcome.Denied) {
      return of(null);
    }

    const docvDenied = isJoint
      ? kycApplicants.filter(app =>
        this.processService.alloyDocVOutcomeMap.get(app.applicantId!) === AlloyOutcome.Denied)
      : [];

    // Joint, one KYC Denied: the application is Rejected, but the other applicant already got a DocV SMS/QR, so let them finish first.
    const kycDenied = kycApplicants.filter(app => this.isKycDenied(app));
    if (isJoint && kycDenied.length > 0 && kycDenied.length < kycApplicants.length) {
      const isCurrentDenied = this.isKycDenied(applicant);
      if (isCurrentDenied) this.removeApplicantTab(applicant);

      const result = this.areRemainingDocVOutcomesRecorded()
        ? this.routeDeniedOrManualReview(AlloyOutcome.Denied, kycDenied[0], ServiceTypeEnum.AlloyKyc, applicationLogRequest)
        : of(null);

      // Stop only after the result completes, otherwise it would cancel the routing below.
      const isCurrentDocVDenied = serviceType === ServiceTypeEnum.AlloyDocV
        && status === AlloyOutcome.Denied
        && docvDenied.some(app => app.applicantId === applicant.applicantId);
      return isCurrentDenied || isCurrentDocVDenied
        ? result.pipe(finalize(() => this.stopApplicantStatusPolling(applicant.applicantId!)))
        : result;
    }

    // A joint DocV denial does not end the journey until every other eligible applicant finishes DocV.
    if (docvDenied.length > 0) {
      const result = this.areRemainingDocVOutcomesRecorded()
        ? this.routeDeniedOrManualReview(
          AlloyOutcome.Denied, docvDenied[0], ServiceTypeEnum.AlloyDocV, applicationLogRequest)
        : of(null);
      const isCurrentDocVDenied = serviceType === ServiceTypeEnum.AlloyDocV
        && status === AlloyOutcome.Denied
        && docvDenied.some(app => app.applicantId === applicant.applicantId);
      return isCurrentDocVDenied
        ? result.pipe(finalize(() => this.stopApplicantStatusPolling(applicant.applicantId!)))
        : result;
    }

    // Joint DocV Manual Review: Under Review, but only after every other eligible applicant finishes DocV. A denial above wins.
    const docvManualReview = isJoint
      ? kycApplicants.filter(app =>
        this.processService.alloyDocVOutcomeMap.get(app.applicantId!) === AlloyOutcome.ManualReview)
      : [];
    if (docvManualReview.length > 0) {
      const result = this.areRemainingDocVOutcomesRecorded()
        ? this.routeDeniedOrManualReview(
          AlloyOutcome.ManualReview, docvManualReview[0], ServiceTypeEnum.AlloyDocV, applicationLogRequest)
        : of(null);
      const isCurrentDocVManualReview = serviceType === ServiceTypeEnum.AlloyDocV
        && status === AlloyOutcome.ManualReview
        && docvManualReview.some(app => app.applicantId === applicant.applicantId);
      return isCurrentDocVManualReview
        ? result.pipe(finalize(() => this.stopApplicantStatusPolling(applicant.applicantId!)))
        : result;
    }

    switch (status) {
      case AlloyOutcome.Denied:
      case AlloyOutcome.ManualReview: {
        // Same as Evaluate: a KYC Manual Review still lets the customer finish DocV, then routes to Under Review.
        if (serviceType === ServiceTypeEnum.AlloyKyc && status === AlloyOutcome.ManualReview
          && !this.areAllDocVOutcomesApproved()) {
          return of(null);
        }

        return this.routeDeniedOrManualReview(status, applicant, serviceType, applicationLogRequest);
      }

      case AlloyOutcome.PreviouslyApproved:
      case AlloyOutcome.Approved:
        // KYC Manual Review takes precedence over an approved DocV (same as Evaluate): route to Under Review.
        const kycManualReviewApplicant = this.applicantRequests.find(
          app => this.processService.alloyEvaluateOutcomeMap.get(app.applicantId!) === AlloyOutcome.ManualReview);
        if (serviceType === ServiceTypeEnum.AlloyDocV && kycManualReviewApplicant && this.areAllDocVOutcomesApproved()) {
          return this.routeDeniedOrManualReview(
            AlloyOutcome.ManualReview, kycManualReviewApplicant, ServiceTypeEnum.AlloyKyc, applicationLogRequest);
        }

        if (serviceType !== ServiceTypeEnum.AlloyJourney && this.isApplicantVerificationComplete(applicant)) {
          this.stopApplicantStatusPolling(applicant.applicantId!);
        }

        this.processService.triggerAlloyJourneyProcessComplete();
        break;

      default:
        break;
    }

    return of(null);
  }

  // A DocV Denied / Manual Review applicant keeps the tab and sees the same "Document Upload Complete" message as an approved one.
  isDocumentUploadFinished(applicant: ApplicantRequest): boolean {
    const docVOutcome = this.processService.alloyDocVOutcomeMap.get(applicant.applicantId!);
    return this.alloyJourneyApplicantStatusMap.get(applicant.applicantId!)?.status === AlloyOutcome.Approved
      || docVOutcome === AlloyOutcome.Denied
      || docVOutcome === AlloyOutcome.ManualReview;
  }

  // Not applicantRequests: a Denied applicant is removed from that list (no tab), but still counts for routing.
  private getKycApplicants(): ApplicantRequest[] {
    return this.facadeService.getShoppingCartApplicationLogsRequest().applicants?.filter(app => app.isKyc) ?? [];
  }

  private isKycDenied(applicant: ApplicantRequest): boolean {
    return this.processService.alloyEvaluateOutcomeMap.get(applicant.applicantId!) === AlloyOutcome.Denied;
  }

  // Applicants with a Denied KYC never get a DocV; every other applicant must have a DocV result of any kind.
  private areRemainingDocVOutcomesRecorded(): boolean {
    return this.getKycApplicants()
      .filter(app => !this.isKycDenied(app))
      .every(app => !!this.processService.alloyDocVOutcomeMap.get(app.applicantId!));
  }

  private areAllDocVOutcomesApproved(): boolean {
    return this.getKycApplicants().every(
      app => this.processService.alloyDocVOutcomeMap.get(app.applicantId!) === AlloyOutcome.Approved);
  }

  // Same idea as a bypassed applicant: no DocV step, so no tab.
  private removeApplicantTab(applicant: ApplicantRequest): void {
    const index = this.applicantRequests.findIndex(app => app.applicantId === applicant.applicantId);
    if (index < 0) return;

    this.applicantRequests.splice(index, 1);
    this.activeTabIndex = 0;
  }

  private routeDeniedOrManualReview(
    status: string,
    applicant: ApplicantRequest,
    serviceType: ServiceTypeEnum,
    applicationLogRequest: any
  ): Observable<any> {
    const isDenied = status === AlloyOutcome.Denied;

    // One terminal route per journey: a recorded denial always wins, and Manual Review never replaces a route already started.
    if (this.terminalRouteIsDenied === true || (this.terminalRouteIsDenied === false && !isDenied)) {
      return of(null);
    }

    // A denial supersedes a Manual Review route that has not navigated yet.
    this.terminalRouteSubscription?.unsubscribe();
    this.terminalRouteIsDenied = isDenied;
    applicationLogRequest.status = isDenied ? StatusEnum.Rejected : StatusEnum.UnderReview;

    // Own subscription: stopping the polling streams must not cancel the routing.
    this.terminalRouteSubscription = this.buildTerminalRoute(isDenied, applicant, serviceType, applicationLogRequest)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe();

    return of(null);
  }

  private buildTerminalRoute(
    isDenied: boolean,
    applicant: ApplicantRequest,
    serviceType: ServiceTypeEnum,
    applicationLogRequest: any
  ): Observable<any> {
    return this.facadeService.getApplicationLog(this.sessionId).pipe(
      switchMap(application => {
        const applicantRcCode = application.applicants?.find(app => app.applicantId === applicant.applicantId)?.rcCode;
        // Same as Evaluate: fall back to the Denied / Under Review code (DocV specific for DocV) when leia has not saved one.
        const fallbackCode = serviceType === ServiceTypeEnum.AlloyDocV
          ? (isDenied ? ReferenceCode.DocVDenied : ReferenceCode.DocVUnderReview)
          : (isDenied ? ReferenceCode.AlloyDenied : ReferenceCode.AlloyUnderReview);
        const referenceCode = applicantRcCode
          ? this.getReferenceCodeFromRcCode(applicantRcCode)
          : fallbackCode;

        return from(this.updateApplication(applicationLogRequest)).pipe(
          switchMap(() => isDenied
            ? this.facadeService.processOnboardingError(referenceCode)
            : this.facadeService.processOnboardingUnderReview(referenceCode)),
          tap(() => this.coreService.navigate(`/${ERROR_PATH}`))
        );
      }),
      // A failed log/update call must not leave the customer on the waiting screen.
      catchError(err => {
        this.logError('Route denied or manual review', err);
        // The error page needs a reference code, so initialize a fallback before navigating.
        return this.facadeService.processOnboardingError(ReferenceCode.GeneralFailure).pipe(
          catchError(() => of(null)),
          tap(() => this.coreService.navigate(`/${ERROR_PATH}`))
        );
      }),
      // Routing ends the Journey for everyone; stopping only this applicant could cancel it or leave the other polling.
      finalize(() => this.stopAllApplicantStatusPolling())
    );
  }

  // Log only the step and HTTP status: the raw error can carry the request URL and response body.
  private logError(step: string, err: any): void {
    this.logger.error(`AlloyJourney: ${step} failed`, err?.status);
  }

  private isApplicantVerificationComplete(applicant: ApplicantRequest): boolean {
    const applicantId = applicant.applicantId!;
    const kycOutcome = this.processService.alloyEvaluateOutcomeMap.get(applicantId);
    const kycApproved = kycOutcome === AlloyOutcome.Approved || kycOutcome === AlloyOutcome.PreviouslyApproved;
    const docVApproved = this.processService.alloyDocVOutcomeMap.get(applicantId) === AlloyOutcome.Approved;

    return kycApproved && docVApproved;
  }

  private stopApplicantStatusPolling(applicantId: number): void {
    this.alloyJourneyStatusRefreshIntervalMap.get(applicantId)?.unsubscribe();
    this.alloyJourneyStatusRefreshIntervalMap.delete(applicantId);
  }

  private stopAllApplicantStatusPolling(): void {
    this.alloyJourneyStatusRefreshIntervalMap.forEach(subscription => subscription.unsubscribe());
    this.alloyJourneyStatusRefreshIntervalMap.clear();
  }

  private updateApplication(applicationRequest: ApplicationLogsRequest): Promise<void> {
    this.facadeService.setShoppingCartApplicationLogsRequest(applicationRequest);
    return this.facadeService.updateApplication();
  }

  private getApplication(): Observable<ApplicationDetailLog> {
    return this.facadeService.getApplicationLog(this.sessionId);
  }

  sanitizeImageUrl(imageUrl: string): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(`data:image/svg+xml;base64,${imageUrl}`);
  }

  alloyJourneyTabChange(changeEvent: MatTabChangeEvent) {
    this.activeTabIndex = changeEvent.index;
  }

  ngOnDestroy(): void {
    this.destroySubject$.next();
    this.destroySubject$.complete();
  }
}
